import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type PriceAcknowledgement,
  RXDB_PRICE_ACKNOWLEDGEMENT_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/price-acknowledgement";
import { RxDBContext } from "$global-providers/rxdb-provider/provider";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useUser } from "$global-providers/user-provider";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { RxCollection } from "rxdb";
import { v4 as uuidv4 } from "uuid";

export type PriceReviewState = {
  isReviewMode: boolean;
  /**
   * Caller's last acknowledgement timestamp, ISO string. Null on first
   * visit (no row yet) — pass undefined into the query in that case so
   * the API returns the unfiltered list; the banner still shows but with
   * count 0 (nothing has "moved since you last reviewed" because the
   * clock hasn't started).
   */
  acknowledgedAt: string | null;
  /** Enter review mode by setting `?review=true` (the banner's CTA). */
  enterReviewMode: () => void;
  /** Leave review mode without acknowledging — just removes `?review`. */
  clearReviewMode: () => void;
  /** Persist a new acknowledgement at now() and clear review mode. */
  markAsReviewed: () => Promise<void>;
  /** True while the RxDB write is in flight — used to disable the button. */
  marking: boolean;
};

const REVIEW_PARAM = "review";

function useRxCollectionDocs<T>(collection: RxCollection<T> | null): T[] {
  const [docs, setDocs] = useState<T[]>([]);

  useEffect(() => {
    if (!collection) {
      setDocs([]);
      return;
    }
    const sub = collection.find().$.subscribe((results) => {
      setDocs(results.map((r) => r.toJSON() as T));
    });
    return () => sub.unsubscribe();
  }, [collection]);

  return docs;
}

export const usePriceReviewState = (): PriceReviewState => {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useUser();
  const { getCurrentTime } = useServerTime();
  const rxdbCtx = useContext(RxDBContext);
  const db = rxdbCtx?.db;
  const [rxdbEnabled] = useFeatureFlag<boolean>(
    featureFlags.billing.priceListRxdb,
  );
  // The acknowledgement collection is gated by the same flag as the rest of
  // the price-list RxDB replication; without it the row would never push to
  // the server. Treat the URL flag as a no-op when RxDB is off.
  const ackCollection = rxdbEnabled
    ? ((db?.[RXDB_PRICE_ACKNOWLEDGEMENT_COLLECTION_NAME] as
        | RxCollection<PriceAcknowledgement>
        | undefined) ?? null)
    : null;

  const acks = useRxCollectionDocs<PriceAcknowledgement>(ackCollection);
  const [marking, setMarking] = useState(false);

  const myAck = acks.find((a) => a.userId === user.id);
  const isReviewMode = rxdbEnabled && searchParams.get(REVIEW_PARAM) === "true";

  const setReviewParam = (present: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (present) {
      next.set(REVIEW_PARAM, "true");
    } else {
      next.delete(REVIEW_PARAM);
    }
    setSearchParams(next, { replace: true });
  };

  const enterReviewMode = () => setReviewParam(true);
  const clearReviewMode = () => setReviewParam(false);

  const markAsReviewed = async () => {
    if (!ackCollection || marking) return;
    setMarking(true);
    try {
      // Server-aligned timestamp. `acknowledgedAt` is later compared on
      // the server against `Price.updatedAt` (Prisma `@updatedAt`, server
      // clock); writing a local-clock value here would drift the
      // comparison if the device clock leads the server (PNE-385).
      const now = getCurrentTime().toISOString();
      if (myAck) {
        const doc = await ackCollection.findOne(myAck.id).exec();
        await doc?.patch({
          acknowledgedAt: now,
          meta: {
            ...myAck.meta,
            updatedAt: now,
            updatedByUserId: user.id,
          },
        });
      } else {
        await ackCollection.insert({
          id: uuidv4(),
          userId: user.id,
          acknowledgedAt: now,
          meta: {
            createdAt: now,
            createdByUserId: user.id,
            updatedAt: now,
            updatedByUserId: user.id,
          },
        });
      }
      // Leave review mode once acknowledged. The acknowledgement row itself
      // is what drives the next visit's count, not the URL.
      setReviewParam(false);
    } finally {
      setMarking(false);
    }
  };

  return {
    isReviewMode,
    acknowledgedAt: myAck?.acknowledgedAt ?? null,
    enterReviewMode,
    clearReviewMode,
    markAsReviewed,
    marking,
  };
};
